import { ENV } from '../constants/envs/constants'

/**
 * Feature flag names
 * Add new feature flags here as needed
 */
export enum FeatureFlag {
  SELL_CRYPTO = 'SELL_CRYPTO'
}

/**
 * Environment type matching the application's environment configuration
 */
type Environment = 'local' | 'development' | 'beta' | 'production'

/**
 * Feature flag configuration type
 * Maps each feature flag to the environments where it should be enabled
 */
type FeatureFlagConfig = {
  [key in FeatureFlag]: Environment[]
}

/**
 * Configuration: Define which environments each feature is enabled in
 *
 * SELL_CRYPTO: Only enabled in local and development environments
 * This allows the feature to be developed and tested without exposing it to production users
 */
const FEATURE_FLAG_CONFIG: FeatureFlagConfig = {
  [FeatureFlag.SELL_CRYPTO]: ['local', 'development', 'beta']
}

/**
 * Checks if a feature is enabled in the current environment
 *
 * @param feature - The feature flag to check
 * @returns true if the feature is enabled in the current environment, false otherwise
 *
 * @example
 * ```typescript
 * import { isFeatureEnabled, FeatureFlag } from '@/utils/featureFlags'
 *
 * if (isFeatureEnabled(FeatureFlag.SELL_CRYPTO)) {
 *   // Render Sell Crypto UI
 * }
 * ```
 */
export function isFeatureEnabled(feature: FeatureFlag): boolean {
  const enabledEnvironments = FEATURE_FLAG_CONFIG[feature]

  // Fail-safe: If feature is not configured, default to enabled
  // This prevents accidental feature lockouts due to configuration errors
  if (!enabledEnvironments) {
    return true
  }

  return enabledEnvironments.includes(ENV as Environment)
}
